import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Users as UsersIcon, ShieldOff, ShieldCheck, RotateCcw, Ban, CheckCircle2, Clock,
  History, Loader2, Globe, Smartphone, KeyRound, Unlock, Lock, Copy, Check, Trash2, FilePlus2,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-AU", { timeZone: "Australia/Sydney", year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short" }); } catch { return ts; }
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [tempPw, setTempPw] = useState(null); // {email, password}
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const [u, h] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/login-history?limit=50"),
      ]);
      setUsers(u.data); setHistory(h.data);
    } catch { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const reset2fa = async (u) => {
    if (!window.confirm(`Reset 2FA for ${u.email}? They will set up a new authenticator at next login.`)) return;
    setBusy(u.id + "-2fa");
    try { await api.post(`/admin/users/${u.id}/reset-2fa`); toast.success("2FA reset — new QR at next login"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
    finally { setBusy(""); }
  };

  const setStatus = async (u, active) => {
    setBusy(u.id + "-status");
    try { await api.post(`/admin/users/${u.id}/status`, { active }); toast.success(active ? "User activated" : "User deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(""); }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? A temporary password will be generated to share with them.`)) return;
    setBusy(u.id + "-pw");
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setTempPw({ email: data.email, password: data.temporary_password });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
    finally { setBusy(""); }
  };

  const unlock = async (u) => {
    setBusy(u.id + "-unlock");
    try { await api.post(`/admin/users/${u.id}/unlock`); toast.success("Account unlocked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Unlock failed"); }
    finally { setBusy(""); }
  };

  const togglePresales = async (u) => {
    const next = !u.can_create_presales;
    setBusy(u.id + "-presales");
    try {
      await api.post(`/admin/users/${u.id}/presales-access`, { allowed: next });
      toast.success(next ? `${u.email} can now create Pre-Sales projects` : `Pre-Sales access removed for ${u.email}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(""); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? Their account and sign-in history will be removed. Any projects they own will be reassigned to you. This cannot be undone.`)) return;
    setBusy(u.id + "-del");
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      toast.success(data.projects_reassigned > 0
        ? `User deleted — ${data.projects_reassigned} project(s) reassigned to you`
        : "User deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(""); }
  };

  const copyTemp = async () => {
    try { await navigator.clipboard.writeText(tempPw.password); setCopied(true); setTimeout(() => setCopied(false), 1800); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="p-8 lg:p-10 max-w-5xl">
      <div className="border-b border-white/10 pb-6 mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-[#484F58] mb-2 font-mono-jb">Admin</div>
        <h1 className="font-mono-jb text-3xl sm:text-4xl font-extrabold tracking-tighter text-white flex items-center gap-3">
          <UsersIcon className="text-[#00F0FF]" size={32} /> User Management
        </h1>
        <p className="text-[#8B949E] mt-3 leading-relaxed">Manage everyone with access. Reset 2FA if someone loses their phone, or deactivate accounts when people leave.</p>
      </div>

      {loading ? (
        <div className="font-mono-jb text-[#8B949E] text-sm">Loading...</div>
      ) : (
        <>
          <div className="space-y-3 mb-12" data-testid="users-list">
            {users.map((u) => {
              const admin = u.role === "admin";
              return (
                <div key={u.id} data-testid={`user-row-${u.id}`}
                  className={`border p-4 flex items-center gap-4 transition-colors duration-200 ${u.active ? "border-white/10 bg-[#0B0C10] hover:border-white/25" : "border-[#FF3B30]/25 bg-[#FF3B30]/5"}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono-jb text-sm text-white truncate">{u.email}</span>
                      <span className="text-[9px] uppercase tracking-[0.2em] font-mono-jb px-1.5 py-0.5 border" style={{ color: admin ? "#00FF41" : "#00F0FF", borderColor: admin ? "rgba(0,255,65,0.3)" : "rgba(0,240,255,0.3)" }}>{u.role}</span>
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 text-[10px] font-mono-jb text-[#8B949E] flex-wrap">
                      <span className="flex items-center gap-1">{u.active ? <CheckCircle2 size={11} className="text-[#00FF41]" /> : <Ban size={11} className="text-[#FF3B30]" />}{u.active ? "Active" : "Deactivated"}</span>
                      {u.locked && <span className="flex items-center gap-1 text-[#FF3B30]"><Lock size={11} /> Locked</span>}
                      <span className="flex items-center gap-1"><Smartphone size={11} className={u.totp_enabled ? "text-[#00FF41]" : "text-[#EAE04A]"} />{u.totp_enabled ? "2FA on" : "2FA pending"}</span>
                      <span className="flex items-center gap-1"><Clock size={11} /> Last: {fmt(u.last_login)}</span>
                      {!admin && u.can_create_presales && (
                        <span className="flex items-center gap-1 text-[#EAE04A]"><FilePlus2 size={11} /> Pre-Sales access</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    {!admin && (
                      <Button data-testid={`presales-access-${u.id}`} onClick={() => togglePresales(u)} disabled={busy === u.id + "-presales"} variant="outline"
                        className={`rounded-sm bg-transparent font-mono-jb text-[10px] uppercase tracking-wider h-9 ${u.can_create_presales ? "border-[#EAE04A]/50 text-[#EAE04A] hover:bg-[#EAE04A]/10 hover:text-[#EAE04A]" : "border-white/15 text-white hover:bg-white/5 hover:text-[#EAE04A]"}`}>
                        {busy === u.id + "-presales" ? <Loader2 className="animate-spin" size={13} /> : <><FilePlus2 size={13} className="mr-1" /> {u.can_create_presales ? "Revoke Pre-Sales" : "Allow Pre-Sales"}</>}
                      </Button>
                    )}
                    {u.locked && (
                      <Button data-testid={`unlock-${u.id}`} onClick={() => unlock(u)} disabled={busy === u.id + "-unlock"}
                        className="rounded-sm bg-[#00FF41] text-black hover:bg-[#00FF41] font-mono-jb text-[10px] uppercase tracking-wider h-9">
                        {busy === u.id + "-unlock" ? <Loader2 className="animate-spin" size={13} /> : <><Unlock size={13} className="mr-1" /> Unlock</>}
                      </Button>
                    )}
                    {!admin && (
                      <Button data-testid={`reset-pw-${u.id}`} onClick={() => resetPassword(u)} disabled={busy === u.id + "-pw"} variant="outline"
                        className="rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-[#00F0FF] font-mono-jb text-[10px] uppercase tracking-wider h-9">
                        {busy === u.id + "-pw" ? <Loader2 className="animate-spin" size={13} /> : <><KeyRound size={13} className="mr-1" /> Reset Password</>}
                      </Button>
                    )}
                    <Button data-testid={`reset-2fa-${u.id}`} onClick={() => reset2fa(u)} disabled={busy === u.id + "-2fa"} variant="outline"
                      className="rounded-sm border-[#EAE04A]/40 bg-transparent text-[#EAE04A] hover:bg-[#EAE04A]/10 hover:text-[#EAE04A] font-mono-jb text-[10px] uppercase tracking-wider h-9">
                      {busy === u.id + "-2fa" ? <Loader2 className="animate-spin" size={13} /> : <><RotateCcw size={13} className="mr-1" /> Reset 2FA</>}
                    </Button>
                    {!admin && (
                      u.active ? (
                        <Button data-testid={`deactivate-${u.id}`} onClick={() => setStatus(u, false)} disabled={busy === u.id + "-status"}
                          className="rounded-sm bg-[#FF3B30]/90 text-white hover:bg-[#FF3B30] font-mono-jb text-[10px] uppercase tracking-wider h-9">
                          {busy === u.id + "-status" ? <Loader2 className="animate-spin" size={13} /> : <><ShieldOff size={13} className="mr-1" /> Deactivate</>}
                        </Button>
                      ) : (
                        <Button data-testid={`activate-${u.id}`} onClick={() => setStatus(u, true)} disabled={busy === u.id + "-status"}
                          className="rounded-sm bg-[#00FF41] text-black hover:bg-[#00FF41] font-mono-jb text-[10px] uppercase tracking-wider h-9">
                          {busy === u.id + "-status" ? <Loader2 className="animate-spin" size={13} /> : <><ShieldCheck size={13} className="mr-1" /> Activate</>}
                        </Button>
                      )
                    )}
                    {!admin && (
                      <Button data-testid={`delete-user-${u.id}`} onClick={() => deleteUser(u)} disabled={busy === u.id + "-del"} variant="outline"
                        className="rounded-sm border-[#FF3B30]/40 bg-transparent text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] font-mono-jb text-[10px] uppercase tracking-wider h-9">
                        {busy === u.id + "-del" ? <Loader2 className="animate-spin" size={13} /> : <><Trash2 size={13} className="mr-1" /> Delete</>}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-2 mb-4">
            <History className="text-[#00F0FF]" size={18} />
            <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Recent Sign-ins</h2>
          </div>
          {history.length === 0 ? (
            <div className="text-[#484F58] text-sm font-mono-jb py-6 text-center border border-dashed border-white/10">No sign-ins recorded yet.</div>
          ) : (
            <div className="border border-white/10 bg-[#0B0C10]" data-testid="login-history">
              {history.map((e, i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-3 border-b border-white/5 last:border-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00FF41] shrink-0" />
                  <div className="font-mono-jb text-sm text-white flex-1 min-w-0 truncate">{e.email}</div>
                  <div className="font-mono-jb text-[11px] text-[#8B949E] flex items-center gap-1.5"><Globe size={11} /> {e.ip || "—"}</div>
                  <div className="font-mono-jb text-[11px] text-[#484F58] w-44 text-right">{fmt(e.at)}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={!!tempPw} onOpenChange={(o) => !o && setTempPw(null)}>
        <DialogContent className="bg-[#0B0C10] border-white/10 text-white" data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono-jb uppercase tracking-wider text-[#00F0FF] flex items-center gap-2"><KeyRound size={16} /> Temporary Password</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <p className="text-sm text-[#8B949E] leading-relaxed">
              Share this temporary password with <span className="text-white">{tempPw?.email}</span> offline. They'll be
              asked to set their own password on next sign in.
            </p>
            <div className="flex gap-2">
              <Input data-testid="temp-password-value" readOnly value={tempPw?.password || ""}
                className="bg-[#0B0C10] border-white/15 font-mono-jb text-sm focus-visible:ring-[#00F0FF]" />
              <Button data-testid="copy-temp-password" onClick={copyTemp}
                className="rounded-sm bg-[#00F0FF] text-black hover:bg-[#00F0FF] font-mono-jb text-[11px] uppercase shrink-0 h-10">
                {copied ? <Check size={15} /> : <Copy size={15} />}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
